export type DomainSwitchStateMapEntry = {
    /** The safe section-root state to navigate to when switching domains from this deep state. */
    to: string;
    /** The feature flag that must be active on the target domain to use `to`. If inactive, fall back to dashboard. */
    feature: string;
};

/**
 * Maps deep/detail states to the safe "section root" state (and required feature flag) to navigate
 * to when switching domains.
 *
 * When a user switches domains via the domain picker while on a deep state that uses
 * domain-specific path params (pageId, checkId, fastTrackId), those IDs are invalid on
 * the new domain and would cause 404 errors. Instead of deleting the params and
 * trying to load the same deep state, we navigate to the section root — but only if the
 * target domain has the required feature active; otherwise we fall back to the dashboard.
 *
 * To add a new mapping: add an entry here. No other code changes needed.
 */
export const DOMAIN_SWITCH_STATE_MAP: Record<string, DomainSwitchStateMapEntry> = {
    // Issues by Page section
    'base.customer.domain.accessibility.page-checks': { to: 'base.customer.domain.accessibility.issues-by-page', feature: 'accessibility' },
    'base.customer.domain.accessibility.code-snippets-by-page': { to: 'base.customer.domain.accessibility.issues-by-page', feature: 'accessibility' },

    // Issues by Type section
    'base.customer.domain.accessibility.affected-pages': { to: 'base.customer.domain.accessibility.issues-by-type', feature: 'accessibility' },
    'base.customer.domain.accessibility.affected-page-by-issue': { to: 'base.customer.domain.accessibility.issues-by-type', feature: 'accessibility' },

    // Fast Track section
    'base.customer.domain.accessibility.code-snippet': { to: 'base.customer.domain.accessibility.fast-track', feature: 'accessibility' },
    'base.customer.domain.accessibility.fast-track-check': { to: 'base.customer.domain.accessibility.fast-track', feature: 'accessibility' },

    // PDFs section
    'base.customer.domain.accessibility.pdfs-report': { to: 'base.customer.domain.accessibility.pdfs', feature: 'accessibility' },
    'base.customer.domain.accessibility.pdfs-report-checks': { to: 'base.customer.domain.accessibility.pdfs', feature: 'accessibility' },
    'base.customer.domain.accessibility.pdf-locate-issues': { to: 'base.customer.domain.accessibility.pdfs', feature: 'accessibility' },
};

/**
 * Maps state-name prefixes to the feature flag they represent.
 * Used to look up the required feature for any state that starts with the given prefix
 * but does not have a direct entry in DOMAIN_SWITCH_STATE_MAP.
 */
export const DOMAIN_SWITCH_STATE_PREFIXES: Record<string, string> = {
    'base.customer.domain.accessibility': 'accessibility',
};

/** The set of param names that are domain-specific and must be stripped when using DOMAIN_SWITCH_STATE_MAP. */
export const DOMAIN_SWITCH_STRIP_PARAMS: readonly string[] = ['pageId', 'checkId', 'fastTrackId', 'search', 'sort_by'] as const;
